import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { isVisible, setTabIndex } from './utils';
import { UseExpanderProps, UseExpanderReturnType } from './useExpander.types';

// animation duration in milliseconds
const ANIMATION_DURATION = 250;

const ALL_FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export const useExpander = <T extends HTMLElement>({
  /**
   * True if expander functionality should be suppressed. False otherwise.
   */
  disable = false,
  /**
   * True if the panel is expanded. False otherwise.
   */
  expanded = false,
  /**
   * The minimum height for which an element should be set. This value must be a number of pixels
   */
  minHeight = 0,
}: UseExpanderProps = {}): UseExpanderReturnType<T> => {
  const ref = useRef<null | T>(null);
  const isFirstRun = useRef(true);
  const [internalExpanded, setInternalExpanded] = useState(expanded);

  const toggle = useCallback(
    (newExpanded: boolean) => {
      if (newExpanded !== undefined && newExpanded !== null) {
        setInternalExpanded(!!newExpanded);
      } else {
        setInternalExpanded((previous: boolean) => !previous);
      }
    },
    [setInternalExpanded]
  );

  useLayoutEffect(() => {
    if (!disable && isFirstRun.current === true && ref?.current?.style) {
      ref.current.style.height = internalExpanded ? 'auto' : `${minHeight}px`;
      ref.current.style.overflow = internalExpanded ? 'visible' : 'hidden';
    }
  }, [disable, internalExpanded, minHeight]);

  useEffect(() => {
    if (disable) {
      return;
    }

    let timeout: ReturnType<typeof setTimeout>;
    if (!isFirstRun.current && ref?.current?.style) {
      const scrollHeight = ref.current.scrollHeight ?? minHeight;

      if (internalExpanded) {
        ref.current.style.height = `${scrollHeight}px`;
        timeout = setTimeout(() => {
          if (ref?.current?.style) {
            ref.current.style.height = 'auto';
            ref.current.style.overflow = 'visible';
          }
        }, ANIMATION_DURATION);
      } else {
        ref.current.style.height = `${scrollHeight}px`;
        timeout = setTimeout(() => {
          if (ref?.current?.style) {
            ref.current.style.height = `${minHeight}px`;
            ref.current.style.overflow = 'hidden';
          }
        }, 50); // 50 ms seems to be the right amount of time to wait here. Any less and the UI doesn't pickup the close transition sometimes.
      }
    }
    isFirstRun.current = false;
    return (): void => clearTimeout(timeout);
  }, [disable, internalExpanded, minHeight]);

  useEffect(() => {
    if (disable) {
      return;
    }

    const setTabIndexes = (): void => {
      const node = ref.current;
      const focusableContent = node
        ? node.querySelectorAll<HTMLElement>(ALL_FOCUSABLE_SELECTOR)
        : [];
      focusableContent.forEach((childElement: HTMLElement) => {
        const isTabbable = internalExpanded || isVisible(node, childElement);
        const newTabIndex = isTabbable ? 0 : -1;
        setTabIndex(childElement, newTabIndex);
      });
    };
    // Wait for the amount of time the animation takes so offsets wille be accurate
    const timeout = setTimeout(setTabIndexes, ANIMATION_DURATION);
    return (): void => clearTimeout(timeout);
  }, [disable, internalExpanded]);

  return {
    isExpanded: internalExpanded,
    toggle,
    ref,
  };
};
